import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest, fileToBase64, ApiRequestError } from '../api/client.js';
import { useSession } from '../auth/SessionProvider.js';
import { PERMISSIONS } from '../../shared/permissions.js';
import { Alert, Button, Field, Select, TextInput } from './ui.js';
import { formatBytes } from '../lib/format.js';

const CATEGORY_OPTIONS = [
  { value: 'request_photo', label: 'Photo of the problem' },
  { value: 'investigation_photo', label: 'Investigation photo' },
  { value: 'proposal_backup', label: 'Proposal backup' },
  { value: 'subcontractor_quote', label: 'Subcontractor quote' },
  { value: 'completion_photo', label: 'Completion photo' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'other', label: 'Other' },
] as const;

export function AttachmentUploader({
  workOrderId,
  defaultCategory = 'other',
  defaultVisibility = 'customer_visible',
}: {
  workOrderId: string;
  defaultCategory?: string;
  defaultVisibility?: 'customer_visible' | 'internal';
}) {
  const queryClient = useQueryClient();
  const { can, isContractor } = useSession();
  const inputRef = useRef<HTMLInputElement>(null);
  const [category, setCategory] = useState(defaultCategory);
  const [visibility, setVisibility] = useState(defaultVisibility);
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [uploaded, setUploaded] = useState(0);

  const canUploadInternal = isContractor && can(PERMISSIONS.ATTACHMENT_UPLOAD_INTERNAL);

  const upload = useMutation({
    mutationFn: async (selected: File[]) => {
      // Uploaded one at a time so a single rejected file does not discard the rest,
      // and so progress is visible on a slow connection.
      let count = 0;
      for (const file of selected) {
        await apiRequest(`/api/work-orders/${workOrderId}/attachments`, {
          method: 'POST',
          body: {
            fileName: file.name,
            mimeType: file.type || 'application/octet-stream',
            contentBase64: await fileToBase64(file),
            category,
            visibility,
            description: description.trim() || undefined,
          },
        });
        count += 1;
        setUploaded(count);
      }
      return count;
    },
    onSuccess: () => {
      setFiles([]);
      setDescription('');
      setUploaded(0);
      if (inputRef.current) inputRef.current.value = '';
      void queryClient.invalidateQueries({ queryKey: ['attachments', workOrderId] });
    },
    onError: (caught: unknown) => {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'The upload could not be completed. Please try again.',
      );
      setUploaded(0);
    },
  });

  return (
    <div className="flex flex-col gap-3">
      {error && <Alert variant="error">{error}</Alert>}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Document type" htmlFor="attachment-category">
          <Select
            id="attachment-category"
            value={category}
            onChange={(event) => setCategory(event.target.value)}
          >
            {CATEGORY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>

        {canUploadInternal && (
          <Field
            label="Visibility"
            htmlFor="attachment-visibility"
            hint="Internal documents are never shown to USPS users."
          >
            <Select
              id="attachment-visibility"
              value={visibility}
              onChange={(event) =>
                setVisibility(event.target.value as 'customer_visible' | 'internal')
              }
            >
              <option value="customer_visible">Visible to USPS</option>
              <option value="internal">VGS internal only</option>
            </Select>
          </Field>
        )}

        <Field label="Description" htmlFor="attachment-description" className="sm:col-span-2">
          <TextInput
            id="attachment-description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Optional note about these files"
          />
        </Field>
      </div>

      <Field
        label="Files"
        htmlFor="attachment-files"
        hint="PDF, JPG, PNG, HEIC, WEBP, DOC, DOCX, XLS, XLSX, or CSV. Up to 25 MB each."
      >
        <input
          ref={inputRef}
          id="attachment-files"
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp,.doc,.docx,.xls,.xlsx,.csv"
          onChange={(event) => {
            setError(null);
            setFiles(Array.from(event.target.files ?? []));
          }}
          className="block w-full text-sm text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-medium file:text-slate-800 hover:file:bg-slate-200"
        />
      </Field>

      {files.length > 0 && (
        <ul className="flex flex-col gap-1 text-sm text-slate-700">
          {files.map((file) => (
            <li key={`${file.name}-${file.size}`} className="flex justify-between gap-3">
              <span className="truncate">{file.name}</span>
              <span className="shrink-0 tabular-nums text-slate-500">{formatBytes(file.size)}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center gap-3">
        <Button
          onClick={() => {
            setError(null);
            upload.mutate(files);
          }}
          disabled={files.length === 0}
          loading={upload.isPending}
        >
          Upload {files.length > 0 ? `${files.length} file${files.length === 1 ? '' : 's'}` : ''}
        </Button>
        {upload.isPending && files.length > 1 && (
          <span className="text-sm text-slate-600">
            {uploaded} of {files.length} uploaded
          </span>
        )}
      </div>
    </div>
  );
}
